import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {makeAutoObservable, observable, reaction} from "mobx";
import {ModalType, RequestState, RoundStatus} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {IUserStore} from "data/stores/user/user.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IPlayersStore} from "data/stores/players/players.store";
import type {IRoundsStore, ISession} from "data/stores/rounds/rounds.store";
import type {ITeamStore} from "data/stores/team/team.store";
import type {IRankingsStore} from "data/stores/rankings/rankings.store";
import {isEqual} from "lodash";

interface IHeaderCopy {
	title: string;
	description: string;
	sub_description: string;
}

export interface IGameController extends ViewController {
	isLoading: boolean;

	get i18n(): ILocalizationStore;

	get selectedSession(): ISession | undefined;

	get isLoadingTeam(): boolean;

	get headerCopyObj(): IHeaderCopy;

	onOpenScoreModal: () => void;
}

@injectable()
export class GameController implements IGameController {
	@observable isLoading: boolean = false;
	@observable _requestState: RequestState = RequestState.IDLE;

	private _disposerReaction!: ReturnType<typeof reaction>;
	private _disposerReactionTrades!: ReturnType<typeof reaction>;
	private _disposerReactionSession!: ReturnType<typeof reaction>;

	get i18n() {
		return this._i18nStore;
	}

	get isLoadingTeam() {
		return this._teamStore.isLoadingTeam;
	}

	get selectedSession() {
		return this._roundStore.selectedSession;
	}

	get missedSession() {
		return (
			this._roundStore.selectedRound?.status !== RoundStatus.Scheduled &&
			!this._teamStore.teamObject.id
		);
	}

	get headerCopyObj() {
		const isLastDay = isEqual(this._roundStore.selectedRoundId, 3);

		if (this.missedSession) {
			return {
				title: this._i18nStore.t("page.game.title_missed", "YOU HAVE MISSED THIS SESSION"),
				description: "",
				sub_description: "",
			};
		}

		if (
			[
				this._roundStore.selectedRoundSessions.length > 1,
				this._teamStore.isTradeStarted,
			].every(Boolean)
		) {
			return {
				title: this._i18nStore.t("page.game.title_3", "EDIT YOUR TEAM"),
				description: this._i18nStore.t(
					"page.game.description_3",
					"You have 3 trades to use to make changes to your PM session team"
				),
				sub_description: "",
			};
		}

		return {
			[RoundStatus.Scheduled]: this._teamStore.teamObject.id
				? {
						title: this._i18nStore.t("page.game.title_2", "YOUR TEAM"),
						description: isLastDay
							? this._i18nStore.t("page.game.description_day_3", "Day 3 Description")
							: this._i18nStore.t(
									"page.game.description_2",
									"You have unlimited trades to use before the start of the session"
							  ),
						sub_description: "",
				  }
				: {
						title: this._i18nStore.t("page.game.title_1", "Build your team"),
						description: this._i18nStore.t(
							"page.game.description_1",
							"Select your six players for today’s AM session."
						),
						sub_description: isLastDay
							? ""
							: this._i18nStore.t(
									"page.game.description_1_part_2",
									"Come back once the session has started to edit your PM session."
							  ),
				  },
			[RoundStatus.Playing]: {
				title: this._i18nStore.t("page.game.title_4", "YOUR TEAM"),
				description: "",
				sub_description: "",
			},
			[RoundStatus.Complete]: {
				title: this._i18nStore.t("page.game.title_4", "YOUR TEAM"),
				description: "",
				sub_description: "",
			},
		}[this._roundStore.selectedRound?.status ?? RoundStatus.Scheduled];
	}

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.ModalsStore) private _modalStore: IModalsStore,
		@inject(Bindings.PlayersStore) private _playerStore: IPlayersStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.TeamStore) private _teamStore: ITeamStore,
		@inject(Bindings.RankingsStore) private _rankingStore: IRankingsStore
	) {
		makeAutoObservable(this);
	}

	setIsLoading(value: boolean): void {
		this.isLoading = value;
	}

	onOpenScoreModal = () => {
		this._modalStore.showModal(ModalType.SCORE_INFO, {
			message: "",
		});
	};

	dispose() {
		this._disposerReaction?.();
		this._disposerReactionTrades?.();
		this._disposerReactionSession?.();
	}

	private showSessionModal() {
		const isSessionModalShown = Boolean(
			JSON.parse(localStorage.getItem("isSessionModalShown") || "null")
		);

		if (!isSessionModalShown) {
			this._modalStore.showModal(ModalType.INFO_SESSION);
			localStorage.setItem("isSessionModalShown", "1");
		}
	}

	async init(param: void) {
		this.setIsLoading(true);

		await this._roundStore.fetchRounds();
		await this._playerStore.fetchPlayers();

		if (this._roundStore.actualRound?.id) {
			this._roundStore.setSelectedRoundId(this._roundStore.actualRound.id);
			this._roundStore.setSelectedRoundSessions();
			this._roundStore.setSelectedSessionId(
				this._roundStore.selectedRoundSessions[0].id ?? 0
			);
		}

		if (this._roundStore.selectedSessionId) {
			await this._teamStore.getTeam({
				roundId: this._roundStore.selectedSessionId,
			});
		}

		this._disposerReaction = reaction(
			() => this._roundStore.selectedSessionId,
			(sessionId) => {
				if (sessionId) {
					void this._teamStore.getTeam({
						roundId: sessionId,
					});
				}
			},
			{fireImmediately: true}
		);

		this._disposerReactionTrades = reaction(
			() => this._teamStore.isTradeStarted && this._teamStore.isCurrentLineupHasPlayer,
			(isTradeAvailable) => {
				const isTradeStartedModalShown = Boolean(
					JSON.parse(localStorage.getItem("isTradeStartedModalShown") || "null")
				);

				if (!this._teamStore.isCurrentLineupHasPlayer) {
					return;
				}

				if (isTradeAvailable && !isTradeStartedModalShown) {
					this._modalStore.showModal(ModalType.INFO_TRADE, {
						message: "",
					});

					localStorage.setItem("isTradeStartedModalShown", "1");
				}
			},
			{fireImmediately: true}
		);

		this._disposerReactionSession = reaction(
			() => this.missedSession,
			(missedSession) => {
				if (missedSession) {
					this.showSessionModal();
				}
			},
			{fireImmediately: true}
		);

		this.setIsLoading(false);
	}
}
